// Formular zum Erstellen/Bearbeiten einer öffentlichen (EE-kuratierten) Playlist.
// Create-Modus: nur Metadaten (Name/Beschreibung/Cover) → nach dem Anlegen wechselt der Parent in
// den Edit-Modus, damit Tracks kuratiert werden können. Edit-Modus: zusätzlich Publish-Toggle +
// Track-Kuratierung (hinzufügen/entfernen/umsortieren). Owner (EE-System) + Authz macht das Backend.
import React, { useEffect, useId, useState } from 'react';
import {
  PhotoIcon,
  TrashIcon,
  PlusIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { BunnyFilePicker } from '../admin/BunnyFilePicker';
import { TrackPicker } from '../playlists/TrackPicker';
import { Rueckfrage } from '../ui/Rueckfrage';
import { useRueckfrage } from '../../hooks/useRueckfrage';
import { playlistService } from '../../services/playlistService';
import { artistService } from '../../services/artistService';
import { Playlist } from '../../types/playlist';
import { Artist } from '../../types/artist';
import { TrackKindLabels } from '../../types/audio';
import type { BenachrichtigungsTyp } from '../ui/Benachrichtigung';

interface PlaylistFormProps {
  initial: Playlist | null; // null = Create-Modus
  onCreated: (p: Playlist) => void; // nach Create → Parent wechselt in Edit-Modus
  onClose: () => void; // „Fertig/Zurück" → Parent zurück zur Liste (+ refresh)
  onDeleted: (id: string) => void;
  /** Meldet an den Balken der Elternseite. Tonlage aus `BenachrichtigungsTyp` — nicht abschreiben. */
  notify: (type: BenachrichtigungsTyp, message: string) => void;
}

export const PlaylistForm: React.FC<PlaylistFormProps> = ({
  initial,
  onCreated,
  onClose,
  onDeleted,
  notify,
}) => {
  const isEdit = !!initial;
  // Siehe `ui/Filterleiste.tsx`: erzeugte Wurzel statt fester Zeichenketten.
  const feldId = useId();
  const [pl, setPl] = useState<Playlist | null>(initial);
  const [name, setName] = useState(initial?.name ?? '');
  // Ersteller-Marke: Pre-Select aus der rohen FK, damit auch ein Interpret im Entwurf-Status
  // vorausgewählt bleibt (artistRef ist status-gated und wäre dann null). '' = keine Marke.
  const [artistId, setArtistId] = useState(initial?.artistId ?? initial?.artistRef?.id ?? '');
  const [artists, setArtists] = useState<Artist[]>([]);
  const [description, setDescription] = useState(initial?.description ?? '');
  const [coverImageUrl, setCoverImageUrl] = useState(initial?.coverImageUrl ?? '');
  const [saving, setSaving] = useState(false);
  // Serialisiert Item-Mutationen (add/remove/reorder/status): verhindert Races bei schnellen Klicks
  // — jede Mutation berechnet aus dem aktuellen pl-State, also darf nur eine gleichzeitig laufen.
  const [mutating, setMutating] = useState(false);
  const [showCoverPicker, setShowCoverPicker] = useState(false);
  const [showTrackPicker, setShowTrackPicker] = useState(false);

  // Sicherheitsabfragen — Feldname `rückfrage`, nicht `inhalt` (das ist für den
  // Benachrichtigungs-Balken belegt). Warum hinter dem `await` nichts veralten kann, steht
  // ausführlich in `AlbumForm.tsx`: der Schleier der Rückfrage deckt die Seite und fängt den
  // Fokus, `mutating` kann also während der Frage nicht umspringen; ein Seitenwechsel beantwortet
  // die offene Frage im Haken mit `false`.
  const { rückfrage, frage, antworte } = useRueckfrage();

  // Kuratierte Interpreten fürs Marken-Dropdown (Admin-Liste, alle Status) — identisch zu AlbumForm.
  useEffect(() => {
    let alive = true;
    artistService
      .getArtists()
      .then((list) => { if (alive) setArtists(list); })
      .catch(() => { /* Dropdown bleibt leer — die Marke ist optional */ });
    return () => { alive = false; };
  }, []);

  const existingTrackIds = new Set((pl?.items ?? []).map((i) => i.trackId));

  const handleSaveMeta = async () => {
    if (!name.trim()) {
      notify('error', 'Name ist erforderlich');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        artistId, // '' = Marke entfernen (Backend leert dann FK + Spiegel), id = setzen
        description: description.trim() || null,
        coverImageUrl: coverImageUrl.trim() || null,
      };
      if (isEdit && pl) {
        const updated = await playlistService.updatePlaylist(pl.id, payload);
        setPl(updated);
        notify('success', 'Gespeichert');
      } else {
        const created = await playlistService.createPlaylist(payload);
        notify('success', 'Playlist erstellt — jetzt Tracks hinzufügen');
        onCreated(created);
      }
    } catch {
      notify('error', 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (status: 'DRAFT' | 'PUBLISHED') => {
    if (!pl || mutating) return;
    // Ohne `gefahr`: Veröffentlichen zerstört nichts, es stellt die Sichtbarkeit um — und
    // derselbe Knopf heißt danach „Zurückziehen" und stellt sie zurück.
    if (status === 'PUBLISHED' && pl.items.length === 0) {
      const trotzdem = await frage({
        titel: 'Playlist ohne Tracks veröffentlichen?',
        text:
          'Diese Playlist enthält noch keine Tracks. Veröffentlicht ist sie im Portal für alle '
          + 'sichtbar, aber leer — es gibt nichts darin zum Anhören.\n\n'
          + 'Rückgängig zu machen ist das jederzeit über „Zurückziehen".',
        bestätigenText: 'Trotzdem veröffentlichen',
      });
      if (!trotzdem) return;
    }
    setMutating(true);
    try {
      const updated = await playlistService.updatePlaylist(pl.id, { status });
      setPl(updated);
      notify('success', status === 'PUBLISHED' ? 'Veröffentlicht' : 'Zurückgezogen (Entwurf)');
    } catch {
      notify('error', 'Statuswechsel fehlgeschlagen');
    } finally {
      setMutating(false);
    }
  };

  const handleAddTrack = async (trackId: string) => {
    if (!pl || mutating) return;
    setMutating(true);
    try {
      const updated = await playlistService.addTrack(pl.id, trackId);
      setPl(updated);
    } catch {
      notify('error', 'Track konnte nicht hinzugefügt werden');
    } finally {
      setMutating(false);
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    if (!pl || mutating) return;
    setMutating(true);
    try {
      const updated = await playlistService.removeItem(pl.id, itemId);
      setPl(updated);
    } catch {
      notify('error', 'Track konnte nicht entfernt werden');
    } finally {
      setMutating(false);
    }
  };

  const move = async (index: number, dir: -1 | 1) => {
    if (!pl || mutating) return;
    const ids = pl.items.map((i) => i.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    setMutating(true);
    try {
      const updated = await playlistService.reorderItems(pl.id, ids);
      setPl(updated);
    } catch {
      notify('error', 'Umsortieren fehlgeschlagen');
    } finally {
      setMutating(false);
    }
  };

  const handleDelete = async () => {
    if (!pl) return;
    // Stand VOR der Frage festhalten und danach benutzen — gelöscht wird genau die Playlist,
    // deren Name im Dialog stand (Begründung ausführlich in `AlbumForm.tsx`).
    const { id, name: nameZurZeitDerFrage } = pl;
    // `gefahr: true` — die einzige unwiderrufliche Stelle dieser Datei. Die Folgen sind am
    // Backend nachgelesen (`playlistService.deletePublicPlaylist`): die `playlist_items`
    // kaskadieren, die dahinterliegenden Spuren bleiben; `deleteLibraryTarget` räumt Merkzeichen
    // und Ordner-Einträge bei ALLEN Nutzern ab — bei einer öffentlichen Playlist wiegt das
    // schwerer als bei einer privaten, weil viele sie gemerkt haben können.
    const bestätigt = await frage({
      titel: 'Playlist endgültig löschen?',
      text:
        `„${nameZurZeitDerFrage}" wird gelöscht. Das lässt sich nicht rückgängig machen.\n\n`
        + 'Die enthaltenen Titel bleiben bestehen — verloren geht die Zusammenstellung. Bei '
        + 'allen, die diese öffentliche Playlist gemerkt oder in einen Ordner gelegt haben, '
        + 'verschwindet sie aus der Bibliothek.',
      gefahr: true,
    });
    if (!bestätigt) return;
    try {
      await playlistService.deletePlaylist(id);
      onDeleted(id);
    } catch {
      notify('error', 'Löschen fehlgeschlagen');
    }
  };

  return (
    <div className="space-y-6">
      {/* Metadaten */}
      <div className="card p-6 space-y-4">
        <div>
          <label htmlFor={`${feldId}-name`} className="block text-sm font-medium text-secondary-700 mb-2">Name *</label>
          <input
            id={`${feldId}-name`}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input w-full"
            placeholder="z. B. Tiefe Entspannung"
          />
        </div>
        {/* Ersteller-Marke. Bewusst NICHT „Interpret" beschriftet, obwohl technisch dieselbe
            Artist-Entität wie beim Album dahintersteht: eine kuratierte Playlist mischt Titel
            verschiedener Interpreten — „Interpret" wäre hier schlicht falsch. Gemeint ist die Marke
            mit Logo, unter der die Playlist herausgegeben wird; sie ersetzt im Portal die feste
            Zeile „Energetic Eternity". Dropdown-Mechanik 1:1 wie AlbumForm (Admin-Liste inkl.
            Entwürfe, leerer Wert = entfernen). */}
        <div>
          <label htmlFor={`${feldId}-marke`} className="block text-sm font-medium text-secondary-700 mb-2">Ersteller-Marke</label>
          <select id={`${feldId}-marke`} value={artistId} onChange={(e) => setArtistId(e.target.value)} className="input w-full">
            <option value="">— keine Marke —</option>
            {artists.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}{a.status !== 'PUBLISHED' ? ' (Entwurf)' : ''}
              </option>
            ))}
          </select>
          <p className="mt-2 text-xs text-secondary-400">
            Steht im Portal an der Stelle des Herausgebers. Ohne Marke zeigt das Portal wie bisher
            „Energetic Eternity". Ein Interpret im Entwurf erscheint nur als Name, ohne Logo und Link.
          </p>
        </div>
        <div>
          <label htmlFor={`${feldId}-beschreibung`} className="block text-sm font-medium text-secondary-700 mb-2">Beschreibung</label>
          <textarea
            id={`${feldId}-beschreibung`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="input w-full"
            placeholder="Worum geht es in dieser Playlist?"
          />
        </div>
        <div>
          {/* Gruppe statt Feld: unter „Cover" stehen nur Schaltflächen, kein Eingabefeld. */}
          <span id={`${feldId}-cover`} className="block text-sm font-medium text-secondary-700 mb-2">Cover</span>
          <div role="group" aria-labelledby={`${feldId}-cover`} className="flex items-center gap-4">
            {coverImageUrl ? (
              <img src={coverImageUrl} alt="Cover" className="h-20 w-20 rounded-lg object-cover" />
            ) : (
              <div className="h-20 w-20 rounded-lg bg-secondary-100 flex items-center justify-center">
                <PhotoIcon className="w-8 h-8 text-secondary-400" />
              </div>
            )}
            <div className="flex flex-col gap-2">
              <button type="button" onClick={() => setShowCoverPicker(true)} className="btn btn-outline">
                Aus Mediathek wählen
              </button>
              {coverImageUrl && (
                <button
                  type="button"
                  onClick={() => setCoverImageUrl('')}
                  className="inline-flex items-center text-sm text-error-600 hover:text-error-800"
                >
                  <XMarkIcon className="w-4 h-4 mr-1" /> Cover entfernen
                </button>
              )}
            </div>
          </div>
          <p className="mt-2 text-xs text-secondary-400">
            Ohne Cover zeigt das Portal automatisch ein 4er-Mosaik aus den ersten Tracks.
          </p>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={handleSaveMeta} disabled={saving} className="btn btn-primary disabled:opacity-50">
            {saving ? 'Speichern…' : isEdit ? 'Änderungen speichern' : 'Playlist anlegen'}
          </button>
          <button type="button" onClick={onClose} className="btn btn-outline">
            {isEdit ? 'Fertig' : 'Abbrechen'}
          </button>
        </div>
      </div>

      {/* Status + Tracks nur im Edit-Modus */}
      {isEdit && pl && (
        <>
          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-secondary-900">Sichtbarkeit</h3>
                <p className="mt-1 text-sm text-secondary-600">
                  {pl.status === 'PUBLISHED' ? (
                    <span className="inline-flex items-center">
                      <span className="badge badge-primary mr-2">Veröffentlicht</span>
                      Im Portal für alle sichtbar.
                    </span>
                  ) : (
                    <span className="inline-flex items-center">
                      <span className="badge badge-secondary mr-2">Entwurf</span>
                      Noch nicht sichtbar — nur hier im CMS.
                    </span>
                  )}
                </p>
              </div>
              {pl.status === 'PUBLISHED' ? (
                <button type="button" onClick={() => setStatus('DRAFT')} disabled={mutating} className="btn btn-outline disabled:opacity-50">
                  Zurückziehen
                </button>
              ) : (
                <button type="button" onClick={() => setStatus('PUBLISHED')} disabled={mutating} className="btn btn-primary disabled:opacity-50">
                  Veröffentlichen
                </button>
              )}
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-secondary-900">
                Tracks <span className="text-secondary-400">({pl.items.length})</span>
              </h3>
              <button type="button" onClick={() => setShowTrackPicker(true)} disabled={mutating} className="btn btn-primary disabled:opacity-50">
                <PlusIcon className="w-5 h-5 mr-2" />
                Track hinzufügen
              </button>
            </div>

            {pl.items.length === 0 ? (
              <p className="py-8 text-center text-secondary-500">
                Noch keine Tracks. Fügen Sie welche über „Track hinzufügen" hinzu.
              </p>
            ) : (
              <ul className="divide-y divide-secondary-100">
                {pl.items.map((item, index) => (
                  <li key={item.id} className="flex items-center gap-3 py-3">
                    <span className="w-6 text-right text-sm text-secondary-400">{index + 1}</span>
                    {item.coverImage ? (
                      <img src={item.coverImage} alt="" className="h-10 w-10 rounded object-cover" />
                    ) : (
                      <div className="h-10 w-10 rounded bg-secondary-100 flex items-center justify-center">
                        <PhotoIcon className="w-5 h-5 text-secondary-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-sm font-medium text-secondary-900">{item.title}</div>
                      <div className="text-xs text-secondary-500">
                        {TrackKindLabels[item.kind] || item.kind}
                        {item.artist ? ` · ${item.artist}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => void move(index, -1)}
                        disabled={mutating || index === 0}
                        className="p-1 text-secondary-500 hover:text-secondary-800 disabled:opacity-30"
                        title="Nach oben"
                      >
                        <ArrowUpIcon className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void move(index, 1)}
                        disabled={mutating || index === pl.items.length - 1}
                        className="p-1 text-secondary-500 hover:text-secondary-800 disabled:opacity-30"
                        title="Nach unten"
                      >
                        <ArrowDownIcon className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleRemoveItem(item.id)}
                        disabled={mutating}
                        className="p-1 text-error-500 hover:text-error-700 disabled:opacity-30"
                        title="Entfernen"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleDelete}
              className="inline-flex items-center text-sm text-error-600 hover:text-error-800"
            >
              <TrashIcon className="w-4 h-4 mr-1" /> Playlist löschen
            </button>
          </div>
        </>
      )}

      <BunnyFilePicker
        isOpen={showCoverPicker}
        onClose={() => setShowCoverPicker(false)}
        fileType="image"
        title="Cover auswählen"
        onSelect={(url) => {
          setCoverImageUrl(url);
          setShowCoverPicker(false);
        }}
      />

      {showTrackPicker && pl && (
        <TrackPicker
          existingTrackIds={existingTrackIds}
          onAdd={handleAddTrack}
          onClose={() => setShowTrackPicker(false)}
        />
      )}

      {/* Steht bei den beiden anderen Overlays dieses Formulars; die Darstellung hängt nicht an
          der Stelle im Baum, weil der Baustein sich per Portal an `document.body` hängt. Ohne
          offene Frage rendert er nichts. */}
      <Rueckfrage rückfrage={rückfrage} onAntwort={antworte} />
    </div>
  );
};
